import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid, Typography, SvgIcon, Button } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { DateTime } from "luxon";
import _ from "lodash";
import { KeyValueDisplay } from "../../../../components/Typography";
import { RiderContext } from "../../../../context";
import { spTheme } from "../../../../spTheme";
import { useTestIds } from "../../../../hooks/useTestIds";
import Table from "../../../../components/Layout/Table";
import uuidFromUrl from "../../../../util/strings/uuidFromUrl";

const useStyles = makeStyles((theme) => ({
  marginBottom: { marginBottom: theme.spacing(1) },
  tableHead: {
    "& *": {
      background: theme.palette.background.paper,
      fontSize: "0.85em",
      whiteSpace: "nowrap",
    },
  },
  phoneCell: {
    position: "relative",
    paddingLeft: theme.spacing(3),
  },
  deletedPhone: {
    fontSize: "0.85em",
    letterSpacing: "0.8px",
    color: spTheme.palette.text.secondary,
    opacity: 0.5,
  },
  activePhoneIndicator: {
    position: "absolute",
    left: 0,
    top: 25,
  },
  detailsTable: {
    marginTop: theme.spacing(2),
  },
  relatedDevices: {
    textAlign: "center",
  },
  firstAccess: {
    textAlign: "left",
  },
  verified: {
    textAlign: "center",
  },
  phoneTypography: {
    textAlign: "left",
    opacity: 0.8,
  },
  clickableNumber: {
    textAlign: "left",
    textDecoration: "underline",
    paddingLeft: 0,
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
      color: theme.palette.primary.button,
    },
  },
}));

const DeviceDetails = ({ device }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles(theme);
  const testIds = useTestIds("rider-profile-manage-devices-modal");

  const { activeRider } = useContext(RiderContext);
  const tableColumns = [
    {
      canonicalName: "phone_number",
      name: t("Riders.Wallet.Devices.PhoneNumber"),
      tableCellProps: { className: classes.phoneCell },
      renderCellContent: (row) => (
        <Grid item wrap="nowrap" alignItems="center">
          {row.phone_number === activeRider?.phone_number && (
            <SvgIcon
              fontSize="small"
              htmlColor={theme.palette.statusColors.available}
              className={classes.activePhoneIndicator}
            >
              <circle cx="12" cy="12" r="6" />
            </SvgIcon>
          )}
          {row.phone_number && row.phone_number_verified ? (
            <Button
              className={classes.clickableNumber}
              component={RouterLink}
              to={`/riders/${uuidFromUrl(row.url)}/reservations`}
            >
              {row.phone_number}
            </Button>
          ) : (
            <span className={!row.phone_number ? classes.deletedPhone : classes.phoneTypography}>
              {row.phone_number || `[${t("Riders.Wallet.Devices.Deleted")}]`}
            </span>
          )}
        </Grid>
      ),
    },
    {
      canonicalName: "device_count",
      name: t("Riders.Wallet.Devices.RelatedDevices"),
      tableCellProps: { className: classes.relatedDevices },
    },
    {
      canonicalName: "date_joined",
      name: t("Riders.Wallet.Devices.FirstAccess"),
      renderCellContent: (row) => DateTime.fromISO(row.date_joined).toFormat("MM/dd/yyyy"),
      tableCellProps: { className: classes.firstAccess },
    },
    {
      canonicalName: "phone_number_verified",
      name: t("Riders.Wallet.Devices.Verified"),
      renderCellContent: (row) => _.capitalize(String(row.phone_number_verified)),
      tableCellProps: { className: classes.verified },
    },
  ];

  return (
    <Grid item>
      <Grid item xs={12} className={classes.marginBottom}>
        <Typography data-testid={testIds.model} variant="h6">
          {device.model}
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.marginBottom}>
        <KeyValueDisplay
          label={t("Riders.Wallet.Devices.DeviceId")}
          text={device.id}
          labelMinWidth="25%"
          copyToClipboard
          testIds={{
            label: testIds.deviceIdLabel,
            text: testIds.deviceIdValue,
            clipboard: testIds.deviceIdClipboardButton,
          }}
        />
      </Grid>
      <Grid item xs={12} className={classes.marginBottom}>
        <KeyValueDisplay
          label={t("Riders.Wallet.Devices.OS")}
          text={device.os_name}
          labelMinWidth="25%"
          testIds={{
            label: testIds.osLabel,
            text: testIds.osValue,
          }}
        />
      </Grid>
      <Grid item xs={12} className={classes.marginBottom}>
        <KeyValueDisplay
          label={t("Riders.Wallet.Devices.Version")}
          text={device.app_version}
          labelMinWidth="25%"
          testIds={{
            label: testIds.appVersionLabel,
            text: testIds.appVersionValue,
          }}
        />
      </Grid>
      <Grid item xs={12} className={classes.marginBottom}>
        <KeyValueDisplay
          label={t("Riders.Wallet.Devices.Accounts")}
          labelMinWidth="25%"
          tooltip={t("Riders.Wallet.Devices.Tooltip")}
          testIds={{
            label: testIds.accountsLabel,
            tooltipIcon: testIds.accountsTooltipIcon,
          }}
        />
      </Grid>

      <Grid item xs={12} className={classes.detailsTable}>
        <Table
          muiTableProps={{ stickyHeader: true, size: "medium" }}
          rows={device.users}
          columns={tableColumns}
          hidePagination
        />
      </Grid>
    </Grid>
  );
};

DeviceDetails.propTypes = {
  device: PropTypes.object,
};

DeviceDetails.defaultProps = {
  device: {},
};

export default DeviceDetails;
